/**
 * 第 3 步「身份核验」（2026-10-18 三次改版：只留"器械"，签名回归表单）
 * -----------------------------------------------------------------------------
 * 这一步的入选标准不是「重要」，而是**非要用一件器械才能产出**：
 *
 *   ① 身份证     —— 摄像头 + 蒙版取景 + 人像面 OCR（`IdCardField`）
 *   ② 职业证件照 —— 摄像头现拍 + 头肩蒙版取景，按规格存档后再做 1:1 头像比对
 *   ③ 手机号     —— 短信通道（`PhoneVerifyField`）
 *
 * 四步分工（2026-10-18 三次裁决）：
 *   ① 基本信息     —— 敲字与选选项（后台 basic + education 两组）
 *   ② 材料与承诺   —— 上传件 + 手写签名（后台 extra 组，由 `FormRenderer` 渲染）
 *   ③ 身份核验     —— 本文件，全部要「举起手机对着拍 / 收一条短信」
 *   ④ 确认提交     —— 收件地址 + 金额 + 提交
 *
 * ⚠️ 手写签名**不在这里**：签名在业务上是「对所交材料的承诺」，跟着学历证书、
 * 缴费凭证同一组走（第 2 步）。原先塞在这一页是按"器械"归类，但学员的感受是
 * 「我材料都交了，怎么又要签个字」，而且签完名还得再去拍证件照，动作顺序是断的。
 *
 * 卡序按服务端核验链 `OCR → ID_LOCAL_CHECK → PHOTO_GENERATE → FACE_MATCH →
 * ADMISSION_CHECK` 排：
 *   - OCR 成功且拿到 18 位证件号 → 立刻补跑 ②，否则一按快门就撞 `KYC-401`（409）；
 *   - ② 没通过之前，证件照的「立即拍摄」不渲染（点了必失败，不如说清先做什么）。
 *
 * 硬约束（`06-身份核验.md`）不变：
 *   ① 核验是**提交前**的门槛，界面**没有**「等待审核」；
 *   ② 证件照允许**现拍或从相册选图**（2026-10-26 裁决，见下方 ③ 卡里的说明）；
 *      `captureSource` 仍恒传 `CAMERA`（服务端只认这个值）；
 *   ③ 任一环节未达标**当场**提示重拍 / 重填。
 *
 * 页脚只有「下一步」，它通过 `forwardRef` 暴露的 `submit()` 触发准入判定 ——
 * 不在这条卡流里再塞一颗按钮。准入已通过后再点「下一步」不会重跑判定。
 */
import { type FormFieldDef, KycResult, KycStep } from '@enroll/shared';
import { Toast } from 'antd-mobile';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { describeApiError, kycRedoStepFor, kycStepOf } from '../../api/errors';
import { kycAdmissionCheck, kycFace, kycIdCheck, kycPhoto, kycSteps } from '../../api/h5';
import { useProfileBasic } from '../../api/hooks';
import type { KycStepsView } from '../../api/types';
import { isAccountPhoneVerified, isPhoneVerified } from '../../lib/phone-verified';
import { IdCardField, type IdCardOcrResult } from '../FormRenderer/IdCardField';
import { resolveFieldEditing } from '../FormRenderer/logic';
import { PhoneVerifyField } from '../FormRenderer/PhoneVerifyField';
import { CameraIcon, CheckIcon, SearchIcon } from '../icons';
import { Card, Notice } from '../layout';
import { ProcessingOverlay } from '../Processing';
import { openImagePreview } from '../zoom';
import { CameraCapture } from './CameraCapture';

/** 服务端步骤码 → 界面上该重做的那张卡 */
const STEP_HINT: Record<KycStep, string> = {
  [KycStep.OCR]: '身份证识别',
  [KycStep.ID_LOCAL_CHECK]: '证件号校验',
  [KycStep.FACE_MATCH]: '头像比对',
  [KycStep.PHOTO_GENERATE]: '职业证件照',
  [KycStep.ADMISSION_CHECK]: '完成核验',
};

/**
 * 相似度 / 通过线统一按整数百分比展示。
 *
 * `Math.round` 在这里会造成误读：0.796 四舍五入成 80%，而通过线也是 80%，
 * 学员看到两个 80% 却被告知「未通过」。所以**未达标时向下取一位小数**，
 * 让「79.6% < 80%」这件事在界面上自洽。
 */
function toPercent(value: number, floor = false): string {
  const scaled = value * 100;
  return floor ? (Math.floor(scaled * 10) / 10).toFixed(1) : String(Math.round(scaled));
}

export interface KycFlowProps {
  registrationId: string;
  /** 五步全部通过时回调（父级据此解锁「提交报名」） */
  onPassed: () => void;
  /** 字段定义（key 来自后台配置，不写死） */
  nameField?: FormFieldDef;
  idField: FormFieldDef;
  /** 证件类型：跟随身份证卡一起渲染，默认居民身份证 */
  typeField?: FormFieldDef;
  /**
   * 性别 / 民族：同样并进身份证卡（证件上印着，不必再让学员手点一遍）。
   * 没配这两个字段时卡上不出现这两行。
   */
  genderField?: FormFieldDef;
  ethnicityField?: FormFieldDef;
  phoneField: FormFieldDef;
  /** 当前值（都来自同一份表单答案；签名不在这里，它在第 2 步的表单里） */
  values: {
    name: string;
    idType: string;
    idCardNo: string;
    gender: string;
    ethnicity: string;
    phone: string;
  };
  /** 写回表单答案；patch 用语义键，由父级翻译成真实字段 key */
  onChange: (patch: {
    name?: string;
    idType?: string;
    idCardNo?: string;
    gender?: string;
    ethnicity?: string;
    phone?: string;
  }) => void;
  /** 提交后的编辑分级（`REJECTED` 时证件号可能锁定） */
  graded?: boolean;
  /** 本次已获准修改的字段 key（只有 `review` 档会用到；`locked` 永远禁改） */
  grantedKeys?: readonly string[];
  /**
   * 本次进入页面之前核验就已通过（父级记住了结果）。
   *
   * 组件卸载会把「刚拍的照片 / 刚验的手机」这些**本地即时状态**丢掉，可服务端
   * 的 PASS 记录还在。没有这个开关，学员从确认页退回核验页改个手机号，就得把
   * 证件照再拍一遍 —— 数据明明齐了，界面却说没做。
   */
  alreadyPassed?: boolean;
  /** 被 `KYC-501` 拦下时定位到的环节 */
  initialStep?: KycStep;
  /** 与 `initialStep` 配套的失败原因 */
  initialNotice?: string;
}

/** 页脚「下一步」通过 ref 触发准入判定 */
export interface KycFlowHandle {
  submit: () => void;
}

export const KycFlow = forwardRef<KycFlowHandle, KycFlowProps>(function KycFlow(
  {
    registrationId,
    onPassed,
    nameField,
    idField,
    typeField,
    genderField,
    ethnicityField,
    phoneField,
    values,
    onChange,
    graded = false,
    grantedKeys,
    alreadyPassed = false,
    initialStep,
    initialNotice,
  }: KycFlowProps,
  ref,
) {
  /**
   * 等待态存的是**文案**而不是布尔值。
   *
   * 证件照这一步要连打两个外部接口（生成 → 比对），学员需要知道「现在卡在哪一段」；
   * 只给一个转圈，等 5 秒不知道在干什么，就会被当成死机。存文案让浮层跟着阶段变。
   */
  const [busyText, setBusyText] = useState<string | null>(null);
  const busy = busyText !== null;
  const [failText, setFailText] = useState<string | null>(initialNotice ?? null);
  /** 被 KYC-501 打回时，高亮真正需要重做的那张卡 */
  const [redo] = useState<KycStep | null>(initialStep ?? null);
  const redoProfile = redo === KycStep.OCR || redo === KycStep.ID_LOCAL_CHECK;
  const redoPhoto = redo === KycStep.FACE_MATCH || redo === KycStep.PHOTO_GENERATE;

  const [ocr, setOcr] = useState<IdCardOcrResult | null>(null);
  /**
   * 性别 / 民族的必填错误。
   *
   * 这两项被搬进身份证卡（见 `IdCardField` 文件头 ⑤），于是**不再受第 1 步的
   * 表单校验覆盖** —— 门禁必须在这里补一次。否则「OCR 没读出民族」要一路溜到
   * 确认页点提交时才报错，而那时学员早已离开这张卡、还得点步骤条回来找。
   * 错误就地显示在卡里的那一行下方。
   */
  const [fieldErrors, setFieldErrors] = useState<{ gender?: string; ethnicity?: string }>({});
  /** 已通过 ② 的**那一个号码**：改号即失效，不能只存布尔值 */
  const [checkedFor, setCheckedFor] = useState<string | null>(null);
  // 只存本页用得到的字段：④ 展示分数与是否达标、③ 展示图，别把整份出参塞进 state
  const [face, setFace] = useState<{
    similarity: number;
    threshold: number;
    passed: boolean;
  } | null>(null);
  const [photo, setPhoto] = useState<{ photoUrl: string } | null>(null);
  /** 本页刚收码通过的**那个号码**：改号即失效，所以存号码不存布尔值 */
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  /**
   * 账号资料：用于「同号已验过」的免验判断。
   *
   * 少了这一条会出现死锁：`PhoneVerifyField` 判定「账号号已验证」时**不渲染**
   * 「获取验证码」按钮，而门禁若只认本地 `phoneVerified`，卡片就写着「待验证」、
   * 页面上又没有任何可点的验证入口，学员被卡在第三步无处可去。
   * 服务端 `PHONE-005` 走 `PhoneVerificationService.isAccountPhoneVerified`
   * 同一口径，前端这里只是把界面与之一致。
   */
  const profile = useProfileBasic();
  /** 准入判定已通过：再点「下一步」直接放行，不重复打接口 */
  const [passed, setPassed] = useState(alreadyPassed);
  /** 服务端已有的核验状态（进页面拉一次）：刷新后靠它把头像 / 分数 / 有效期找回来 */
  const [restored, setRestored] = useState<KycStepsView | null>(null);

  const idEditing = resolveFieldEditing(idField, graded, grantedKeys);
  const idNumber = values.idCardNo.trim();
  const idReady = idNumber.length >= 15;
  const idChecked = checkedFor !== null && checkedFor === idNumber;
  /** 核验已通过的视角：三张卡都算「已通过」，不因为本地状态丢失而回退成「未完成」 */
  const profileDone = passed || Boolean(restored?.ocr);
  const photoDone = passed || photo !== null;
  /**
   * 手机号已验：三条来源合成 —— 本次核验已通过（服务端 PASS 记录在）、
   * 本页刚收码通过、账号号本身已验证过。
   *
   * 第三条与 `PhoneVerifyField` 内部用的是**同一个函数**，不再各写一遍。
   */
  const accountPhoneVerified = isAccountPhoneVerified(profile.data ?? null, values.phone);
  const phoneDone =
    passed ||
    isPhoneVerified({
      verifiedFor: verifiedPhone,
      phone: values.phone,
      profile: profile.data ?? null,
    });

  /**
   * 进页面回显一次。
   *
   * 核验是**提交前**跑完的，图与分数早就落在服务端了 —— 没有这一步，学员一刷新
   * 就只剩一个「已通过」，头像、有效期、比对分数全看不见，像白做了一遍。
   * 回显失败不阻断主流程（学员仍可重新采集），所以只吞不报。
   */
  useEffect(() => {
    let cancelled = false;
    kycSteps(registrationId)
      .then((view) => {
        if (cancelled) return;
        setRestored(view);
        if (view.photoUrl) setPhoto({ photoUrl: view.photoUrl });
        if (view.face) setFace(view.face);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [registrationId]);

  /** ② 已通过过就不必再打一次：把「通过的号码」补上，改号即自动失效的语义不变 */
  useEffect(() => {
    if (!restored || idNumber.length < 15) return;
    const passedIdCheck = restored.steps.some(
      (item) => item.step === KycStep.ID_LOCAL_CHECK && item.result === KycResult.PASS,
    );
    if (passedIdCheck) setCheckedFor(idNumber);
  }, [restored, idNumber]);

  const handleFailure = (error: unknown) => {
    const message = describeApiError(error);
    setFailText(message);
    Toast.show({ icon: 'fail', content: message });
    if (error instanceof ApiError) {
      const step = kycStepOf(error);
      if (step) setFailText(`${message}（需重做：${STEP_HINT[step]}）`);
    }
  };

  const run = async (text: string, task: () => Promise<void>) => {
    setBusyText(text);
    setFailText(null);
    try {
      await task();
    } catch (error) {
      handleFailure(error);
    } finally {
      setBusyText(null);
    }
  };

  /** ② 证件号本地校验（服务端落 PASS 记录后才允许拍照） */
  const checkIdCard = async (candidate: string) => {
    const value = candidate.trim();
    await kycIdCheck(registrationId, value);
    setCheckedFor(value);
  };

  /** 已经自动跑过 ② 的号码：同一个号码只跑一次，改号才重跑 */
  const autoCheckedRef = useRef<string | null>(null);

  /**
   * 识别成功、且证件号填全 → 自动补 ②。
   * 少了这一步，学员一拍证件照就会被 `KYC-401`（前置未通过）挡回来。
   *
   * ⚠️ 忙碌态**不能在 `finally` 里被 `cancelled` 守卫**（2026-10-26 真机事故）：
   * 本 effect 自己在成功后会 `setCheckedFor`，那次更新让依赖里的 `idChecked` 变 true，
   * effect 随即重跑并在开头 return —— 清理函数先把 `cancelled` 置 true，
   * 于是这次请求的 `finally` 跳过 `setBusyText(null)`，浮层**永久**停在
   * 「正在校验证件号」。服务端那边其实 14ms 就回了 200，学员看到的转圈是假象。
   *
   * 修法两条：① `finally` 无条件清（组件卸载后 setState 在 React 18 无害）；
   * ② 用 `autoCheckedRef` 防重入，别再指望 `idChecked` —— 它正是打断本次流程的那个状态。
   */
  useEffect(() => {
    if (!ocr || !idReady || idChecked) return;
    if (autoCheckedRef.current === idNumber) return;
    autoCheckedRef.current = idNumber;
    void (async () => {
      setBusyText('正在校验证件号');
      try {
        await kycIdCheck(registrationId, idNumber);
        setCheckedFor(idNumber);
      } catch (error) {
        // 失败放行重试：「下一步」仍会再打一次，这里只是不再自动连打
        if (autoCheckedRef.current === idNumber) autoCheckedRef.current = null;
        setFailText(describeApiError(error));
      } finally {
        setBusyText(null);
      }
    })();
  }, [ocr, idReady, idChecked, idNumber, registrationId]);

  /**
   * 只跑 ④ 头像比对（③ 已存档时用）。
   *
   * 单独拆出来是因为**重拍是有上限的**（`KYC-303`，5 次）：服务侧一次抖动
   * （`KYC-201`，503）就逼人重拍，等于拿学员的重拍额度去赔一次服务故障。
   */
  const compareFace = async () => {
    const faceResult = await kycFace(registrationId);
    setFace(faceResult);
  };

  /**
   * 拍一张 → **先存档证件照、再比对**。
   *
   * 顺序不能反：④ 头像比对的输入就是 ③ 刚入库的那张证件照，
   * 先比对会因「证件照还不存在」被守卫拦成 `KYC-401`。
   *
   * 两段都要各自的文案：生成要跑检测 + 人像分割 + 合成（通常 3~5 秒），
   * 比对再 0.5~1 秒。中间不换文案的话，学员会在第 4 秒开始怀疑是不是卡住了。
   */
  const doPhotoAndFace = (image: Blob) =>
    run('正在生成证件照', async () => {
      const photoResult = await kycPhoto(registrationId, image);
      setPhoto(photoResult);
      setBusyText('正在比对证件头像');
      await compareFace();
    });

  const doAdmission = () =>
    run('正在校验核验结果', async () => {
      const result = await kycAdmissionCheck(registrationId);
      if (result.passed) {
        setPassed(true);
        Toast.show({ icon: 'success', content: '核验通过' });
        onPassed();
      } else {
        const redoStep = kycRedoStepFor(result.failCode);
        const message = result.failReason
          ? `${result.failReason}，修改后重新提交`
          : '核验未达标，请按提示重新拍摄身份证 / 人脸后再次提交';
        setFailText(message);
        Toast.show({ icon: 'fail', content: message });
        if (redoStep) setFailText(`${message}（需重做：${redoStep}）`);
      }
    });

  /** 三件事都齐了才允许跑准入判定；缺哪一项就说哪一项 */
  useImperativeHandle(ref, () => ({
    submit: () => {
      if (busy) return;
      /**
       * 性别 / 民族先查，**放在 `passed` 短路之前**：
       * 从确认页退回来的学员不会重跑核验，但那两格仍可能被他刚才手动清空。
       * 位置若放在后面，「已通过」的捷径就会把它俩一起绕过去。
       */
      const textErrors: { gender?: string; ethnicity?: string } = {};
      if (genderField?.required && !values.gender.trim()) {
        textErrors.gender = `请选择${genderField.label}`;
      }
      if (ethnicityField?.required && !values.ethnicity.trim()) {
        textErrors.ethnicity = `请填写${ethnicityField.label}`;
      }
      setFieldErrors(textErrors);
      if (textErrors.gender || textErrors.ethnicity) {
        Toast.show({
          icon: 'fail',
          content: textErrors.gender ?? textErrors.ethnicity ?? '请补全信息',
        });
        return;
      }
      /**
       * 核验已通过（这次进入页面之前就通了，或刚通过）→ 直接放行。
       * 不再重跑 OCR / 证件照 / 短信的门禁：那些 PASS 记录在服务端，
       * 本地丢了本地状态不等于没做。
       */
      if (passed) {
        onPassed();
        return;
      }
      if (!ocr && !profileDone) {
        Toast.show({ icon: 'fail', content: '请先拍摄或上传身份证人像面' });
        return;
      }
      if (!idReady) {
        Toast.show({ icon: 'fail', content: '请补填完整的 18 位身份证号' });
        return;
      }
      if (!idChecked) {
        void run('正在校验证件号', async () => {
          await checkIdCard(values.idCardNo);
          Toast.show({ icon: 'success', content: '证件号校验通过，请拍摄职业证件照' });
        });
        return;
      }
      if (!photoDone) {
        Toast.show({ icon: 'fail', content: '请先拍摄职业证件照' });
        return;
      }
      if (!phoneDone) {
        Toast.show({ icon: 'fail', content: '请先完成手机号短信验证' });
        return;
      }
      void doAdmission();
    },
  }));

  return (
    <div className="kyc-flow">
      {failText ? <Notice tone="danger">{failText}</Notice> : null}

      {/* 等待反馈：这几步都要真人等 2~5 秒，光靠按钮变灰等于「点了没反应」 */}
      <ProcessingOverlay
        visible={busy}
        title={busyText ?? ''}
        hint="通常需要几秒钟，请勿重复点击"
      />

      {/* ① 身份证（正反面 + OCR） */}
      <div className={redoProfile ? 'kyc-redo' : undefined}>
        <IdCardField
          registrationId={registrationId}
          nameField={nameField}
          idField={idField}
          typeField={typeField}
          genderField={genderField}
          ethnicityField={ethnicityField}
          idType={values.idType}
          name={values.name}
          idCardNo={values.idCardNo}
          gender={values.gender}
          ethnicity={values.ethnicity}
          genderError={fieldErrors.gender}
          ethnicityError={fieldErrors.ethnicity}
          disabled={busy || idEditing.disabled}
          verified={profileDone}
          preset={restored?.ocr ?? null}
          presetBackUrl={restored?.idCardBackUrl ?? null}
          hints={[idEditing.hint].filter((hint): hint is string => Boolean(hint))}
          onRecognized={(result) => setOcr(result)}
          onChange={(patch) => {
            // 一动手改就把那一格的旧错误撤掉：留着「请填写民族」而学员正瞪着
            // 已填好的民族，是最典型的那种「界面不信你」
            if (patch.gender !== undefined) {
              setFieldErrors((prev) => ({ ...prev, gender: undefined }));
            }
            if (patch.ethnicity !== undefined) {
              setFieldErrors((prev) => ({ ...prev, ethnicity: undefined }));
            }
            onChange(patch);
          }}
        />
      </div>

      {/* ② 职业证件照（拍完先存档，再由服务端拿它做头像比对） */}
      <Card className={`kyc-card${redoPhoto ? ' kyc-card--redo' : ''}`}>
        <div className="kyc-card__head">
          <span className="kyc-card__title">
            职业证件照
            {/* 不是表单字段，但它是准入链的硬门槛（必须现拍），标星与表单字段同一套 */}
            <em className="field__required">*</em>
          </span>
          <span className={`kyc-badge${photoDone ? ' kyc-badge--ok' : ''}`}>
            {photo ? '已完成' : photoDone ? '已通过' : '未完成'}
          </span>
        </div>
        <div className="kyc-photo">
          <div className={`kyc-photo__frame${photo ? ' kyc-photo__frame--done' : ''}`}>
            {photo ? (
              // 整框做成按钮：缩略图 82×110，「头发有没有出框、脸有没有偏」在这么小的
              // 尺寸上根本看不出来 —— 而这正是学员当场要判断的两件事。
              <button
                type="button"
                className="zoom-hit zoom-hit--frame"
                aria-label="放大查看职业证件照"
                onClick={() => openImagePreview(photo.photoUrl)}
              >
                <img className="kyc-photo__img" src={photo.photoUrl} alt="职业证件照" />
                <span className="zoom-hit__badge">
                  <SearchIcon size={10} strokeWidth={2.6} />
                </span>
              </button>
            ) : photoDone ? (
              // 已通过但本地没有这张图（离开过页面）——不假装有预览，只标状态
              <>
                <span className="kyc-photo__icon kyc-photo__icon--ok">
                  <CheckIcon size={20} strokeWidth={2.4} />
                </span>
                <span className="kyc-photo__label">已存档</span>
              </>
            ) : (
              <>
                <span className="kyc-photo__icon">
                  <CameraIcon size={22} strokeWidth={1.6} />
                </span>
                <span className="kyc-photo__label">待拍摄</span>
              </>
            )}
          </div>
          <div className="kyc-photo__info">
            {/* 文案与实现一致（2026-10-21）：③ 现在是**真的在线生成** ——
                人脸检测 + 人像分割换底 + 按 67% 头高重构图 + 标准化 295×413；
                仍**不做**美颜 / 换装（`06-身份核验.md` §4）。
                提前说清「会自动合成」也是为了给下面那 3~5 秒的等待做预期 —— 不说，
                学员会以为只是存了张原图。

                2026-10-26：不再写「实时拍摄」「按虚线」—— 微信内没有取景画面，
                这两句在那儿是空承诺（详见下方「可拍也可选」）。 */}
            <p className="kyc-photo__desc">
              正面免冠、摘下口罩与帽子，露出双肩；拍完自动生成一寸白底照
            </p>
            <p className="kyc-photo__warn">可拍摄，也可从相册选择已有的正面照</p>
            {/*
              三态而不是两态（2026-10-21 修）：
                ① 没有结果 —— 不显示任何结论；
                ② 有结果且达标 —— 绿色「通过」+ 分数；
                ③ 有结果但不达标 —— **红色「未通过」**，并给出去向。
              原实现按「face 非 null 就算通过」渲染，于是供应商拒打分（记 0）时
              界面上会出现「头像比对通过 · 相似度 0%」—— 与事实正好相反。
            */}
            {face ? (
              face.passed ? (
                <p className="kyc-note kyc-note--ok">
                  头像比对通过 · 相似度 {toPercent(face.similarity)}%
                  <span className="kyc-note__sub">（通过线 {toPercent(face.threshold)}%）</span>
                </p>
              ) : (
                <p className="kyc-note kyc-note--warn">
                  头像比对未通过 · 相似度 {toPercent(face.similarity, true)}%，低于通过线{' '}
                  {toPercent(face.threshold)}%
                </p>
              )
            ) : null}
            {face && !face.passed ? (
              // 未达标就是「拍出来的人不像证件上的人」，去向是**重拍证件照**（退 ③），
              // 不是重拍身份证 —— 身份证侧已经过质量与风险检测，重拍没有意义。
              <p className="kyc-note">
                请确认拍的是本人，并让光线、角度尽量接近身份证照片；重拍证件照后会自动重新比对
              </p>
            ) : null}
            {/*
              只有**没拿到结论**时才给「重新比对」（2026-10-26 用户裁决）。

              原来是 `photo && !face?.passed`，把「已有结论但分数不达标」也算进去了 ——
              那两张图一个字都没变，重跑必然得到同一个分数，纯烧一次人脸比对额度。
              现在收成 `photo && !face`：只兜「服务抖动没返回分数」（`KYC-201`）这一种，
              那种情况确实该重试，而不是拿学员的 5 次重拍额度去赔一次服务故障。

              有结论但不达标 → 只留「重新拍摄」（下面的 `CameraCapture`），
              重拍会自动重新比对（`doPhotoAndFace`）。
            */}
            {photo && !face ? (
              <>
                <p className="kyc-note">头像比对没有返回结果（服务波动），可以重试，不必重拍</p>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => void run('正在比对证件头像', compareFace)}
                >
                  重新比对
                </button>
              </>
            ) : null}
            {idChecked || photoDone ? (
              <CameraCapture
                hint="请正对镜头，头部居中，露出双肩"
                facing="user"
                mask="portrait"
                /*
                 * 允许从相册选图（2026-10-26 用户裁决）。
                 *
                 * 原口径是「证件照必须现拍」（`captureSource` 恒 CAMERA + 服务端 `KYC-301`），
                 * 理由是「防翻拍」。但代价是微信里根本拿不到实时取景（平台不给
                 * `getUserMedia`），学员只剩一条路，且在部分机型上点了没反应。
                 *
                 * 现在把「真实度」交给 ④ 头像比对（1:1 比对身份证头像）——
                 * 选一张别人拍的照片过不了比对，选自己的旧照也只是省了一次重拍。
                 * `captureSource` 仍按入口记 `CAMERA`（服务端只认这个值），
                 * 因此审计上区分不出「现拍」与「选图」—— 这一点在 T-08 里记着。
                 */
                allowAlbum
                actionText={photo ? '重新拍摄' : '拍摄'}
                disabled={busy}
                triggerClassName="btn btn--primary kyc-photo__btn"
                onCapture={(image) => void doPhotoAndFace(image)}
              >
                {photo ? '重新拍摄' : '立即拍摄'}
              </CameraCapture>
            ) : (
              // 前置没通过时给一句明确的「先做什么」，而不是让人点了一下才发现失败
              <p className="kyc-photo__blocked">完成身份证识别与证件号校验后即可拍摄</p>
            )}
          </div>
        </div>
      </Card>

      {/* ③ 手机号（输入 + 短信验证码） */}
      <Card className="kyc-card">
        <div className="kyc-card__head">
          <span className="kyc-card__title">
            {phoneField.label}
            {phoneField.required ? <em className="field__required">*</em> : null}
          </span>
          <span className={`kyc-badge${phoneDone ? ' kyc-badge--ok' : ''}`}>
            {phoneDone ? '已验证' : '待验证'}
          </span>
        </div>
        <p className="kyc-card__desc">用于接收报名进度与缴费通知，请填写本人常用手机号</p>
        <PhoneVerifyField
          value={values.phone}
          disabled={busy}
          showStatus={false}
          onChange={(next) => onChange({ phone: next })}
          onBlur={() => undefined}
          onVerified={(verified) => setVerifiedPhone(verified)}
        />
        {phoneDone ? (
          <p className="kyc-note kyc-note--ok">
            {accountPhoneVerified && verifiedPhone === null
              ? '该号码已在账号资料中验证过，无需再次验证'
              : '短信验证已通过，提交时无需再次填写'}
          </p>
        ) : null}
      </Card>
    </div>
  );
});
